import React, { useEffect } from "react";
import {
	Route,
	createBrowserRouter,
	createRoutesFromElements,
	RouterProvider,
	Navigate,
} from "react-router-dom";
import { OAuthPopup } from "@tasoskakour/react-use-oauth2";

import { useAppDispatch, useAppSelector } from "./app/hooks";
import { setAspUrl } from "./app/siteSlice";

import Public from "./templates/Public";
import Private from "./templates/Private";
import ErrorPage from "./templates/ErrorPage";
import RequireCourse from "./templates/RequireCourse";
import RouteFromArg from "./templates/RouteFromArg";
import SiteConfig from "./templates/SiteConfig";

import Status from "./features/status/Status";
import Login from "./features/login/Login";
import Logout from "./features/login/Logout";
import Courses from "./features/courses/Courses";
import ManageCourse from "./features/courses/ManageCourse";
import Home from "./features/home/Home";
import Writing from "./features/writing/Writing";
import SelectOrganizer from "./features/writing/organizing/SelectOrganizer";
import Organize from "./features/writing/organizing/Organize";
import Write from "./features/writing/drafting/Write";
import Preview from "./features/writing/drafting/Preview";
import ViewDraftAutosaveHistory from "./features/writing/draftAutosaveHistory/ViewDraftAutosaveHistory";
import PeerReviews from "./features/peerReviews/PeerReviews";
import PeerReview from "./features/peerReviews/PeerReview";
import Lessons from "./features/lessons/Lessons";
import Lesson from "./features/lessons/Lesson";
import Reports from "./features/reports/Reports";
import StudentReports from "./features/reports/student/StudentReports";
import StudentPerformanceReport from "./features/reports/student/performance/PerformanceReport";
import StudentProgressReport from "./features/reports/student/progress/ProgressReport";
import StudentPromptReport from "./features/reports/student/prompt/PromptReport";
import StudentUsageReport from "./features/reports/student/usage/UsageReport";
import CourseReports from "./features/reports/course/CourseReports";
import CourseUsageReport from "./features/reports/course/usage/UsageReport";
import LessonUsageByCategoryReport from "./features/reports/course/usage/LessonUsageByCategoryReport";
import LessonUsageByLessonReport from "./features/reports/course/usage/LessonUsageByLessonReport";
import CoursePerformanceReport from "./features/reports/course/performance/PerformanceReport";
import CoursePlagiarismReport from "./features/reports/course/plagiarism/PlagiarismReport";
import CourseProgressReport from "./features/reports/course/progress/ProgressReport";
import CoursePromptReport from "./features/reports/course/prompt/PromptReport";
import CoursePromptScoresReport from "./features/reports/course/prompt/ScoresByPrompt";
import ScoreReport from "./features/scoreReport/ScoreReport";
import AddEditPrompt from "./features/prompts/AddEditPrompt";
import MyAccount from "./features/account/MyAccount";
import ForgotPassword from "./features/account/ForgotPassword";
import ResetPassword from "./features/account/ResetPassword";
import RegisterProctor from "./features/registration/RegisterProctor";
import Notifications from "./features/notifications/Notifications";
import Impersonation from "./features/impersonation/Impersonation";
import Alerts from "./features/alerts/Alerts";
import TermsOfService from "./features/termsOfService/TermsOfService";

export default function App() {
	const { config } = useAppSelector((state) => state.site);
	const dispatch = useAppDispatch();

	let basename = "/spa";
	let virtualDirectory = "";
	const index = window.location.pathname.indexOf(basename);
	if (index > 0) {
		virtualDirectory = window.location.pathname.substring(0, index);
		basename = `${virtualDirectory}${basename}`;
	}

	const origin = window.location.origin;
	if (!(origin.includes(`localhost:5000`) || origin.includes(`127.0.0.1:5000`))) {
		dispatch(setAspUrl(`${origin}${virtualDirectory}`));
	}

	useEffect(() => {
		if (config) {
			document.title = `${config.siteName}: New Experience`;
			let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");

			if (!link) {
				link = document.createElement("link");
				link.rel = "icon";
				link.type = "image/png";
				document.getElementsByTagName("head")[0].appendChild(link);
			}
			if (link) {
				link.href = `favicon-${config.site}.png`;
			}
		}
	}, [config?.siteName]);

	const routes = createRoutesFromElements(
		<Route path="" element={<RouteFromArg />} errorElement={<ErrorPage />}>
			<Route path="" element={<SiteConfig />}>
				<Route path="oauthCallback" element={<OAuthPopup />} />
				<Route element={<Public />}>
					<Route path="/" element={<Login />} />
					<Route path="login" element={<Navigate replace to="/" />} />
					<Route path="logout" element={<Logout />} />
					<Route path="forgotPassword" element={<ForgotPassword />} />
					<Route path="resetPassword" element={<ResetPassword />} />
					<Route path="register" element={<RegisterProctor />} />
					<Route path="status" element={<Status />} />
				</Route>
				<Route element={<Private />}>
					<Route path="courses" element={<Courses />} />
					<Route element={<RequireCourse />}>
						<Route path="home" element={<Home />} />
						<Route path="writing">
							<Route index element={<Writing />} />
							<Route path="prompt" element={<AddEditPrompt />} />
							<Route path="organizers" element={<SelectOrganizer />} />
							<Route path="organize" element={<Organize />} />
							<Route path="preview" element={<Preview />} />
							<Route path="write" element={<Write />} />
							<Route path="viewDrafts" element={<ViewDraftAutosaveHistory />} />
						</Route>
						<Route path="reviews">
							<Route index element={<PeerReviews />} />
							<Route index path=":id" element={<PeerReview />} />
						</Route>
						<Route path="lessons">
							<Route index element={<Lessons />} />
							<Route path=":codeName" element={<Lesson />} />
						</Route>
						<Route path="reports">
							<Route index element={<Reports />} />
							<Route path="student">
								<Route index element={<StudentReports />} />
								<Route path="performance" element={<StudentPerformanceReport />} />
								<Route path="progress" element={<StudentProgressReport />} />
								<Route path="prompt" element={<StudentPromptReport />} />
								<Route path="usage" element={<StudentUsageReport />} />
							</Route>
							<Route path="course">
								<Route index element={<CourseReports />} />
								<Route path="performance" element={<CoursePerformanceReport />} />
								<Route path="plagiarism" element={<CoursePlagiarismReport />} />
								<Route path="progress" element={<CourseProgressReport />} />
								<Route path="prompt">
									<Route index element={<CoursePromptReport />} />
									<Route path="scores" element={<CoursePromptScoresReport />} />
								</Route>
								<Route path="usage">
									<Route index element={<CourseUsageReport />} />
									<Route
										path="lessonCategory"
										element={<LessonUsageByCategoryReport />}
									/>
									<Route path="lesson" element={<LessonUsageByLessonReport />} />
								</Route>
							</Route>
						</Route>
						<Route path="scoreReport">
							<Route index element={<ScoreReport />} />
						</Route>
						<Route path="course">
							<Route index element={<ManageCourse />} />
						</Route>
					</Route>
					<Route path="myAccount" element={<MyAccount />} />
					<Route path="alerts" element={<Alerts />} />
					<Route path="impersonation" element={<Impersonation />} />
					<Route path="notifications" element={<Notifications />} />
					<Route path="termsOfService" element={<TermsOfService />} />
				</Route>
			</Route>
		</Route>
	);

	const router = createBrowserRouter(routes, {
		basename: basename,
	});

	return <RouterProvider router={router} />;
}
